<template>
  <div>
    <b-row>
      <b-col
        md="9"
        class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
      >
        <p class="h5 mt-50 mr-75 ml-50">
          조회타입
        </p>
        <b-form-radio-group
          v-model="query.isPrefixSum"
          @change="changePrefixSum"
        >
          <b-form-radio :value="true">
            누적
          </b-form-radio>
          <b-form-radio :value="false">
            일별
          </b-form-radio>
        </b-form-radio-group>
        <b-form-datepicker
          v-model="query.startDate"
          class="el-def"
          placeholder="조회기간(Fr)"
          style="width: 240px"
        />
        <label class="ml-50 mr-50">~</label>
        <b-form-datepicker
          v-model="query.endDate"
          class="el-def"
          placeholder="조회기간(To)"
          style="width: 240px"
        />
        <label class="ml-50">(*당일데이터 조회 불가)</label>
      </b-col>
      <b-col
        md="3"
        class="d-flex align-items-center justify-content-end"
      >
        <b-button
          variant="primary"
          class="btn-search"
          @click.prevent="fetchData()"
        >
          조회
        </b-button>
        <b-button
          variant="outline-dark"
          class="btn-search ml-1"
          :disabled="data.length==0"
          @click.prevent="excelDownload()"
        >
          <feather-icon
            icon="DownloadIcon"
            class="mr-50"
          />
          <span>엑셀 다운로드</span>
        </b-button>
      </b-col>
    </b-row>
    <cumulative-grid
      v-if="query.isPrefixSum"
      :data="data"
      :grid-visible="gridVisible"
    />
    <daily-grid
      v-else-if="!query.isPrefixSum"
      :data="data"
      :grid-visible="gridVisible"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import axios from '@axios'
import { onMounted, ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ExcelJS from "exceljs"
import { saveAs } from "file-saver"
import CumulativeGrid from '../grid/CumulativeGrid.vue'
import DailyGrid from '../grid/DailyGrid.vue'

export default {
  components: {
    CumulativeGrid,
    DailyGrid,
  },
  props: {
    liveinfoId: {
      type: Number,
      default: null,
      required: false,
    },
    selectedData: {
      type: Object,
      default: null,
      required: false,
    },
  },
  setup(props) {
    const data = ref([])
    const gridVisible = ref(true)

    onMounted(() => {
      fetchData()
    })

    watch(
      () => props.liveinfoId,
      newVal => {
        fetchData(newVal)
      },
    )

    const toast = useToast()
    const pushToast = (variant, title) => {
      let icon

      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else {
        icon = 'AlertTriangleIcon'
      }

      toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    }

    const query = ref({
      startDate: dayjs().add(-1, 'month').format('YYYY-MM-DD'),
      endDate: dayjs().add(-1, 'day').format('YYYY-MM-DD'),
      isPrefixSum: true,
    })

    const changePrefixSum = () => {
      // if (query.value.isPrefixSum) {
      //   query.value.startDate = dayjs().add(-1, 'month').format('YYYY-MM-DD')
      // } else {
      //   query.value.startDate = dayjs().add(-7, 'day').format('YYYY-MM-DD')
      // }
      query.value.startDate = dayjs().add(-1, 'month').format('YYYY-MM-DD')
      query.value.endDate = dayjs().add(-1, 'day').format('YYYY-MM-DD')
      fetchData()
    }

    const fetchData = (id = props.liveinfoId) => {
      if (id === null) {
        data.value = []
        return
      }

      const visibleDate = '2025-08-28'
      if (props.selectedData?.liveDiEndDate && dayjs(props.selectedData?.liveDiEndDate).isBefore(visibleDate)) {
        gridVisible.value = false
        data.value = []
        return
      }

      if (props.selectedData?.liveDiStartDate && dayjs(props.selectedData?.liveDiStartDate).isBefore(visibleDate)) {
        query.value.startDate = visibleDate
      }

      gridVisible.value = true

      const frDate = dayjs(query.value.startDate)
      const toDate = dayjs(query.value.endDate)

      if (query.value.isPrefixSum && Math.abs(frDate.diff(toDate, 'day')) > 365) {
        pushToast('warning', '조회 기간은 최대 1년을 넘을 수 없습니다.')
        return
      }
      if (query.value.isPrefixSum === false && Math.abs(frDate.diff(toDate, 'day')) > 30) {
        pushToast('warning', '조회 기간은 최대 30일을 넘을 수 없습니다.')
        return
      }
      axios.post(`/fa/statistics-ext/live-di`, {
        startDate: query.value.startDate,
        endDate: query.value.endDate,
        liveInfoId: id,
        isPrefixSum: query.value.isPrefixSum,
      })
        .then(rs => {
          data.value = rs?.data || []
        })
        .catch(err => {
          pushToast('danger', `데이터 조회 중 오류가 발생하였습니다.${err}`)
        })
    }

    const excelDownload = async () => {
      if (query.value.isPrefixSum) cumulativeDownload()
      else dailyDownload()
    }

    const cumulativeDownload = async () => {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet("sheet1")

      worksheet.addRow()

      worksheet.getCell('A1').value = 'ID'
      worksheet.getCell('B1').value = '제약사명'
      worksheet.getCell('C1').value = '제품명'
      worksheet.getCell('D1').value = '라이브DI 노출 메시지'
      worksheet.getCell('E1').value = '라이브DI 노출 기간'
      worksheet.getCell('F1').value = '조회 기간'
      worksheet.getCell('A2').value = props.liveinfoId
      worksheet.getCell('B2').value = props.selectedData.clientName
      worksheet.getCell('C2').value = props.selectedData.brandName
      worksheet.getCell('D2').value = props.selectedData.liveDiTitle
      worksheet.getCell('E2').value = `${dayjs(props.selectedData.liveDiStartDate).format('YYYY-MM-DD')} ~ ${dayjs(props.selectedData.liveDiEndDate).format('YYYY-MM-DD')}`
      worksheet.getCell('F2').value = `${query.value.startDate} ~ ${query.value.endDate}`

      worksheet.getCell('A4').value = '지역'
      worksheet.getCell('B4').value = '진료과'
      worksheet.getCell('C4').value = '라이브 DI 노출 수'
      // worksheet.getCell('D4').value = '라이브 DI 노출 수'
      // worksheet.getCell('E4').value = '라이브 DI 노출 수'
      worksheet.getCell('F4').value = '라이브 DI 클릭 수'
      // worksheet.getCell('G4').value = '라이브 DI 클릭 수'
      // worksheet.getCell('H4').value = '라이브 DI 클릭 수'
      worksheet.getCell('C5').value = '전체'
      worksheet.getCell('D5').value = '의사수'
      worksheet.getCell('E5').value = '의원수'
      worksheet.getCell('F5').value = '전체'
      worksheet.getCell('G5').value = '의사수'
      worksheet.getCell('H5').value = '의원수'

      worksheet.mergeCells(`A4:A5`)
      worksheet.mergeCells(`B4:B5`)
      worksheet.mergeCells(`C4:E4`)
      worksheet.mergeCells(`F4:H4`)

      worksheet.columns = [
        { key: "region", width: 15 },
        { key: "medicalDepartmentName", width: 25 },
        { key: "totalShowCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "doctorShowCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "hcpShowCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "totalClickCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "doctorClickCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "hcpClickCount", width: 15, style: { numFmt: '#,##0' } },
      ]

      // eslint-disable-next-line array-callback-return
      data.value.map(region => {
        if (region.liveDiDepts.length > 0) {
        // eslint-disable-next-line array-callback-return
          region.liveDiDepts.map(dept => worksheet.addRow({
            region: region.regionName,
            medicalDepartmentName: dept.medicalDepartmentName,
            totalShowCount: dept.liveDiStatDetail.totalShowCount,
            doctorShowCount: dept.liveDiStatDetail.doctorShowCount,
            hcpShowCount: dept.liveDiStatDetail.hcpShowCount,
            totalClickCount: dept.liveDiStatDetail.totalClickCount,
            doctorClickCount: dept.liveDiStatDetail.doctorClickCount,
            hcpClickCount: dept.liveDiStatDetail.hcpClickCount,
          }))

          worksheet.addRow({
            region: region.regionName,
            medicalDepartmentName: region.regionName === '(미분류제외) 전체 합' ? '전체 합계' : '지역별 소계',
            totalShowCount: region.subTotal.totalShowCount,
            doctorShowCount: region.subTotal.doctorShowCount,
            hcpShowCount: region.subTotal.hcpShowCount,
            totalClickCount: region.subTotal.totalClickCount,
            doctorClickCount: region.subTotal.doctorClickCount,
            hcpClickCount: region.subTotal.hcpClickCount,
          })
        }
      })

      worksheet.eachRow({ includeEmpty: true }, (row, number) => {
        row.eachCell({ includeEmpty: true }, cell => {
          if (number === 1 || number === 4 || number === 5) {
            // eslint-disable-next-line no-param-reassign
            cell.font = {
              bold: true,
            }

            // eslint-disable-next-line no-param-reassign
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'D3D3D3' },
            }
          }

          // eslint-disable-next-line no-param-reassign
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          }

          // eslint-disable-next-line no-param-reassign
          cell.alignment = {
            vertical: 'middle',
            horizontal: 'left',
          }
        })
      })

      const mimeType = {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }

      const buffer = await workbook.xlsx.writeBuffer()

      const blob = new Blob([buffer], mimeType)

      saveAs(blob, "라이브DI 통계(누적).xlsx")
    }

    const dailyDownload = async () => {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet("sheet1")

      worksheet.addRow()

      worksheet.getCell('A1').value = 'ID'
      worksheet.getCell('B1').value = '제약사명'
      worksheet.getCell('C1').value = '제품명'
      worksheet.getCell('D1').value = '라이브DI 노출 메시지'
      worksheet.getCell('E1').value = '라이브DI 노출 기간'
      worksheet.getCell('F1').value = '조회 기간'
      worksheet.getCell('A2').value = props.liveinfoId
      worksheet.getCell('B2').value = props.selectedData.clientName
      worksheet.getCell('C2').value = props.selectedData.brandName
      worksheet.getCell('D2').value = props.selectedData.liveDiTitle
      worksheet.getCell('E2').value = `${dayjs(props.selectedData.liveDiStartDate).format('YYYY-MM-DD')} ~ ${dayjs(props.selectedData.liveDiEndDate).format('YYYY-MM-DD')}`
      worksheet.getCell('F2').value = `${query.value.startDate} ~ ${query.value.endDate}`

      worksheet.getCell('A4').value = '지역'
      worksheet.getCell('B4').value = '일자'
      worksheet.getCell('C4').value = '진료과'
      worksheet.getCell('D4').value = '라이브 DI 노출 수'
      // worksheet.getCell('E4').value = '라이브 DI 노출 수'
      // worksheet.getCell('F4').value = '라이브 DI 노출 수'
      worksheet.getCell('G4').value = '라이브 DI 클릭 수'
      // worksheet.getCell('H4').value = '라이브 DI 클릭 수'
      // worksheet.getCell('I4').value = '라이브 DI 클릭 수'
      worksheet.getCell('D5').value = '전체'
      worksheet.getCell('E5').value = '의사수'
      worksheet.getCell('F5').value = '의원수'
      worksheet.getCell('G5').value = '전체'
      worksheet.getCell('H5').value = '의사수'
      worksheet.getCell('I5').value = '의원수'

      worksheet.mergeCells(`A4:A5`)
      worksheet.mergeCells(`B4:B5`)
      worksheet.mergeCells(`C4:C5`)
      worksheet.mergeCells(`D4:F4`)
      worksheet.mergeCells(`G4:I4`)

      worksheet.columns = [
        { key: "region", width: 15 },
        { key: "date", width: 15 },
        { key: "medicalDepartmentName", width: 25 },
        { key: "totalShowCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "doctorShowCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "hcpShowCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "totalClickCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "doctorClickCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "hcpClickCount", width: 15, style: { numFmt: '#,##0' } },
      ]

      // eslint-disable-next-line array-callback-return
      data.value.map(row => {
        // 일자가 비어있는 행 = 지역 기간 소계
        const isRegionSubTotal = !row.dateStr

        // eslint-disable-next-line array-callback-return
        row.liveDiDepts.map(dept => worksheet.addRow({
          region: row.regionName,
          date: row.dateStr,
          medicalDepartmentName: dept.medicalDepartmentName,
          totalShowCount: dept.liveDiStatDetail.totalShowCount,
          doctorShowCount: dept.liveDiStatDetail.doctorShowCount,
          hcpShowCount: dept.liveDiStatDetail.hcpShowCount,
          totalClickCount: dept.liveDiStatDetail.totalClickCount,
          doctorClickCount: dept.liveDiStatDetail.doctorClickCount,
          hcpClickCount: dept.liveDiStatDetail.hcpClickCount,
        }))

        let subTotalDate = row.dateStr
        if (isRegionSubTotal) {
          subTotalDate = row.regionName === '(미분류제외) 전체 합' ? '전체 합계' : '지역별 소계'
        }

        worksheet.addRow({
          region: row.regionName,
          date: subTotalDate,
          medicalDepartmentName: isRegionSubTotal ? '' : '일자별 소계',
          totalShowCount: row.subTotal.totalShowCount,
          doctorShowCount: row.subTotal.doctorShowCount,
          hcpShowCount: row.subTotal.hcpShowCount,
          totalClickCount: row.subTotal.totalClickCount,
          doctorClickCount: row.subTotal.doctorClickCount,
          hcpClickCount: row.subTotal.hcpClickCount,
        })
      })

      worksheet.eachRow({ includeEmpty: true }, (row, number) => {
        row.eachCell({ includeEmpty: true }, cell => {
          if (number === 1 || number === 4 || number === 5) {
            // eslint-disable-next-line no-param-reassign
            cell.font = {
              bold: true,
            }

            // eslint-disable-next-line no-param-reassign
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'D3D3D3' },
            }
          }

          // eslint-disable-next-line no-param-reassign
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          }

          // eslint-disable-next-line no-param-reassign
          cell.alignment = {
            vertical: 'middle',
            horizontal: 'left',
          }
        })
      })

      const mimeType = {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }

      const buffer = await workbook.xlsx.writeBuffer()

      const blob = new Blob([buffer], mimeType)

      saveAs(blob, "라이브DI 통계(일별).xlsx")
    }

    return {
      data,
      gridVisible,
      query,
      fetchData,
      changePrefixSum,
      excelDownload,
    }
  },
}
</script>

<style scoped>
.table-bordered th, td {
  border: 1px solid #dee2e6;
}

.table th {
  vertical-align: middle;
}
</style>
